import CreateEditBillingGroupCompleted from "@components/form/billing_group/CreateEditBillingGroupCompleted";
import CreateEditBillingGroupStep1 from "@components/form/billing_group/CreateEditBillingGroupStep1";
import CreateEditBillingGroupStep2 from "@components/form/billing_group/CreateEditBillingGroupStep2";
import WorkflowPanels from "@components/form/WorkflowPanels";
import Layout from "@components/layout/Layout";
import ConfirmModal from "@components/shared/ConfirmModal";
import StepButton from "@components/shared/StepButton";
import { DraftNote, EMPTY_DRAFT_NOTE } from "@components/notes/NoteComposer";
import { flushNoteStepDraft } from "@components/notes/flushNoteStepDraft";
import {
  BillingAndChargeProvider,
  useBillingAndChargeContext,
} from "@context/BillingAndChargeContext";
import { useGetLevel1Resource } from "@hooks/query/useGetLevel1Resource";
import { createBillingGroup } from "@services/billingGroup";
import { DBTables } from "@utils/constants/dbTables";
import { toastWarning } from "@utils/toast";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

const CreateBillingGroup: React.FunctionComponent = () => {
  return (
    <BillingAndChargeProvider>
      <MainComponent />
    </BillingAndChargeProvider>
  );
};

const MainComponent: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const level1ResourceId = searchParams.get("level1ResourceId");
  const [currentStep, setCurrentStep] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const [draftNote, setDraftNote] = useState<DraftNote>(EMPTY_DRAFT_NOTE);
  const {
    billingGroupData,
    setBillingGroupData,
    workflowInstance,
    setWorkflowInstance,
  } = useBillingAndChargeContext();

  const { data: level1Resource } = useGetLevel1Resource(
    level1ResourceId || "",
    { enabled: !!level1ResourceId },
  );
  const { mutateAsync: saveBillingGroupData, isLoading } = useMutation<
    any,
    any,
    {
      name: string;
      level1ResourceId: string;
    }
  >(billingGroup => createBillingGroup(billingGroup));

  const handleStep1Save = (step1Data: any) => {
    setBillingGroupData({
      ...billingGroupData,
      billingGroup: step1Data.billingGroup,
    });

    setCurrentStep(1);
  };

  const handleStep2Save = () => {
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    const { billingGroup } = billingGroupData;
    const { name, level1Resource } = billingGroup;

    try {
      const [billingGroup, workflowInstance] = await saveBillingGroupData({
        name,
        level1ResourceId: level1Resource?.id,
      });

      setBillingGroupData({
        billingGroup: { ...billingGroupData.billingGroup, ...billingGroup },
      });

      setWorkflowInstance(workflowInstance);

      // FR-1.1 + FR-1 invariant: flush optional NoteStep draft AFTER the entity
      // commits. Note failure does NOT roll back the entity; user gets a warning
      // toast and can re-add via the audit-trail panel.
      const noteResult = await flushNoteStepDraft(
        draftNote,
        DBTables.BillingGroups,
        billingGroup?.id,
      );

      setIsModalOpen(false);
      setSuccess(true);

      if (noteResult.kind === "failed") {
        toastWarning(
          t("notes.step.flush_failed", {
            artifact: t("billing_groups.field_name"),
          }),
        );
      }
    } catch (e: any) {
      toast.error(
        <>
          <p>{t("billing_groups.save_failed")}</p>
          <p>{e.message}</p>
        </>,
        {
          className: "toast toast-error",
        },
      );

      setIsModalOpen(false);
    }
  };

  return (
    <Layout
      permissions={["CreateBillingGroups"]}
      breadcrumb={
        level1ResourceId
          ? [
              {
                label: t("dashboard.dashboard"),
                href: "/polestar",
              },
              {
                label: level1Resource?.name,
                href: `/polestar/level1wrs/${level1ResourceId}`,
              },
              {
                label: t("billing_groups.add_billing_group"),
              },
            ]
          : [
              {
                label: t("dashboard.dashboard"),
                href: "/polestar",
              },
              {
                label: t("billing_groups.add_billing_group"),
              },
            ]
      }
      title={t("billing_groups.add_billing_group")}
    >
      <WorkflowPanels
        contentClassName="border-none bg-transparent"
        selectedStep={currentStep}
        steps={[
          {
            label: (
              <StepButton
                index={0}
                currentStep={currentStep}
                isOnChain={success}
                info={
                  <div className="w-full pl-2 flex flex-col gap-2">
                    <dl className="grid grid-cols-1 gap-x-4">
                      <div className="sm:col-span-1">
                        <dt className="text-sm font-bold text-gray-500">
                          {t("billing_groups.field_name")}:
                        </dt>
                        <dd className="mt-1 text-sm text-gray-500">
                          {billingGroupData.billingGroup?.name}
                        </dd>
                      </div>
                    </dl>
                    <dl className="grid grid-cols-1 gap-x-4">
                      <div className="sm:col-span-1">
                        <dt className="text-sm font-bold text-gray-500">
                          {t("billing_groups.field_level1_resource")}:
                        </dt>
                        <dd className="mt-1 text-sm text-gray-500 truncate">
                          {billingGroupData.billingGroup?.level1Resource?.name}
                        </dd>
                      </div>
                    </dl>
                  </div>
                }
              >
                {t("billing_groups.create_step1_title")}
              </StepButton>
            ),
            panel: (
              <CreateEditBillingGroupStep1
                level1Resource={level1Resource}
                billingGroup={billingGroupData?.billingGroup}
                onSave={handleStep1Save}
              />
            ),
          },
          {
            label: (
              <StepButton
                index={1}
                currentStep={success ? 2 : currentStep}
                isOnChain={success}
              >
                {t("billing_groups.create_step2_title")}
              </StepButton>
            ),
            panel: success ? (
              <CreateEditBillingGroupCompleted
                billingGroup={billingGroupData?.billingGroup}
                workflowInstance={workflowInstance}
              />
            ) : (
              <CreateEditBillingGroupStep2
                level1Resource={level1Resource}
                billingGroup={billingGroupData?.billingGroup}
                onSave={handleStep2Save}
                onEditDetails={() => setCurrentStep(0)}
                noteValue={draftNote}
                onNoteChange={setDraftNote}
              />
            ),
          },
        ]}
      />
      <ConfirmModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleSave}
        confirmText={t("common.ledger") as string}
        isSubmitting={isLoading}
      >
        {t("billing_groups.modal_confirmation")}
      </ConfirmModal>
    </Layout>
  );
};

export default CreateBillingGroup;
